import type { HttpRequestConfig, IHttpClient } from "@/core/interfaces";
import {
  collectAllPages,
  extractItems,
  extractTotal,
  isRecord,
  readFirstText,
  readText,
} from "@/core/utils";
import { Registry } from "@/shared/infra/dependency-injection/registry";
import type {
  ListPermissionsParams,
  Permission,
  PermissionsPage,
  UpdatePermissionParams,
} from "./permissions.types";

export const PERMISSION_PATHS = {
  LIST: "/api/plataforma/permissoes",
} as const;

const COLLECTION_KEYS = ["permissoes"];

export function buildPermissionPath(id: string): string {
  return `${PERMISSION_PATHS.LIST}/${encodeURIComponent(id)}`;
}

function getHttpClient(): IHttpClient {
  return Registry.getInstance().inject<IHttpClient>("HttpClient");
}

/** Desembrulha o envelope do BFF (`{ success, data }`) até chegar ao recurso. */
function extractResource(payload: unknown): unknown {
  if (!isRecord(payload)) return payload;

  for (const key of ["data", "permissao"]) {
    const nested = payload[key];
    if (isRecord(nested)) return extractResource(nested);
  }

  return payload;
}

function toPermission(raw: unknown): Permission | null {
  if (!isRecord(raw)) return null;

  const id = readText(raw.id);
  const codigo = readFirstText(raw, "code", "codigo");
  if (!id && !codigo) return null;

  return {
    id: id || codigo,
    codigo,
    nome: readFirstText(raw, "name", "nome"),
    descricao: readFirstText(raw, "description", "descricao"),
  };
}

function byNome(a: Permission, b: Permission): number {
  return a.nome.localeCompare(b.nome, "pt-BR", { sensitivity: "base" });
}

export async function listPermissions({
  pesquisa,
  pagina,
  tamanhoPagina,
  signal,
}: ListPermissionsParams = {}): Promise<PermissionsPage> {
  const term = pesquisa?.trim();

  const params: Record<string, string | number> = {};
  if (term) params.pesquisa = term;
  if (pagina) params.pagina = pagina;
  if (tamanhoPagina) params.tamanhoPagina = tamanhoPagina;

  const config: HttpRequestConfig = { signal };
  if (Object.keys(params).length > 0) config.params = params;

  const response = await getHttpClient().get<unknown>(PERMISSION_PATHS.LIST, config);

  const permissions = extractItems(response.data, COLLECTION_KEYS)
    .map(toPermission)
    .filter((permission): permission is Permission => permission !== null)
    .sort(byNome);

  return { permissions, total: extractTotal(response.data) ?? permissions.length };
}

/**
 * Conjunto completo do resultado da consulta em vigor, percorrido página a
 * página e reordenado ao fim para que a sequência valha sobre todas as páginas.
 */
export async function listAllPermissions(
  params: Omit<ListPermissionsParams, "pagina" | "tamanhoPagina"> = {},
): Promise<Permission[]> {
  const permissions = await collectAllPages((pagina, tamanhoPagina) =>
    listPermissions({ ...params, pagina, tamanhoPagina }).then(({ permissions: items, total }) => ({
      items,
      total,
    })),
  );

  return permissions.sort(byNome);
}

/**
 * Atualiza uma permissão existente. O corpo carrega somente `nome` e `descricao`
 * (RN01); o `codigo` não é aceito pela API.
 *
 * Retorna a permissão devolvida pela API ou `null` quando a resposta não traz
 * corpo utilizável (ex.: 204), cabendo ao chamador reaproveitar o que enviou.
 */
export async function updatePermission({
  id,
  nome,
  descricao,
  signal,
}: UpdatePermissionParams): Promise<Permission | null> {
  const body = { nome: nome.trim(), descricao: descricao.trim() };

  const response = await getHttpClient().put<unknown>(buildPermissionPath(id), body, { signal });

  return toPermission(extractResource(response.data));
}
